import React, { useState, useEffect, useCallback } from 'react';
import { X, RotateCcw, Type, Contrast, BookOpen, Ear, Link2, Keyboard, Palette, AlignJustify } from 'lucide-react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'aja_a11y_prefs';
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL || ''}/api/builder/media/files/brand/ai_joomla_logo_official.png`;

const DEFAULT_PREFS = {
  fontStep: 0,
  lineHeight: false,
  letterSpacing: false,
  dyslexia: false,
  contrast: 'none', // 'none' | 'dark' | 'light'
  monochrome: false,
  saturation: 'none', // 'none' | 'high' | 'low'
  highlightLinks: false,
  highlightHeadings: false,
  keyboardNav: false,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

function Switch({ on, onClick, testId }) {
  return (
    <button type="button" className={`aja-switch ${on ? 'on' : ''}`} onClick={onClick} data-testid={testId} aria-pressed={on}>
      <span className="aja-switch-dot" />
    </button>
  );
}

function Row({ icon: Icon, label, desc, active, children, testId }) {
  return (
    <div className={`aja-row ${active ? 'active' : ''}`} data-testid={testId}>
      <div className="aja-row-icon"><Icon size={16} strokeWidth={2} /></div>
      <div className="aja-row-text">
        <div className="aja-row-label">{label}</div>
        {desc && <div className="aja-row-desc">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

/** AI Joomla Accessibility Widget — the same widget we ship as a Joomla
 * extension, reimplemented here so aijoomla.org itself runs a live,
 * fully-working demo of it. Styled to match the accessiBe-style reference
 * the product is modeled on: blue header/footer bars, white content rows
 * with real ON/OFF switches. */
export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [reading, setReading] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove('aja-font-1', 'aja-font-2', 'aja-font-3');
    if (prefs.fontStep > 0) html.classList.add(`aja-font-${prefs.fontStep}`);

    html.classList.toggle('aja-line-height', prefs.lineHeight);
    html.classList.toggle('aja-letter-spacing', prefs.letterSpacing);
    html.classList.toggle('aja-dyslexia', prefs.dyslexia);
    html.classList.toggle('aja-highlight-links', prefs.highlightLinks);
    html.classList.toggle('aja-highlight-headings', prefs.highlightHeadings);
    html.classList.toggle('aja-keyboard-nav', prefs.keyboardNav);

    html.classList.remove('aja-contrast-dark', 'aja-contrast-light');
    if (prefs.contrast !== 'none') html.classList.add(`aja-contrast-${prefs.contrast}`);

    const filters = [];
    if (prefs.monochrome) filters.push('grayscale(1)');
    if (prefs.saturation === 'high') filters.push('saturate(2)');
    if (prefs.saturation === 'low') filters.push('saturate(0.4)');
    html.style.filter = filters.join(' ');

    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* noop */ }
  }, [prefs]);

  useEffect(() => () => {
    const html = document.documentElement;
    html.className = html.className.replace(/\baja-\S+/g, '').trim();
    html.style.filter = '';
  }, []);

  const set = useCallback((patch) => setPrefs((p) => ({ ...p, ...patch })), []);

  const resetAll = () => {
    window.speechSynthesis?.cancel();
    setReading(false);
    setPrefs(DEFAULT_PREFS);
  };

  const toggleReadAloud = () => {
    if (reading) {
      window.speechSynthesis.cancel();
      setReading(false);
      return;
    }
    const text = document.body.innerText.slice(0, 4000);
    const utter = new SpeechSynthesisUtterance(text);
    utter.onend = () => setReading(false);
    utter.onerror = () => setReading(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    setReading(true);
  };

  return (
    <>
      <button
        type="button"
        className="aja-openbtn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Accessibility options"
        data-testid="aja-widget-toggle"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="11" stroke="#fff" strokeWidth="1.6" />
          <circle cx="12" cy="6.6" r="1.9" fill="#fff" />
          <path d="M5.2 9.4c2.2.9 4.4 1.3 6.8 1.3s4.6-.4 6.8-1.3" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M12 10.7v3.1l-2.6 5.9M12 13.8l2.6 5.9" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="aja-panel" role="dialog" aria-label="Accessibility adjustments" data-testid="aja-widget-panel">
          <div className="aja-panel-header">
            <button className="aja-header-btn" onClick={resetAll} data-testid="aja-widget-reset" aria-label="Reset all settings">
              <RotateCcw size={14} />
            </button>
            <p className="aja-panel-title">Accessibility Adjustments</p>
            <button className="aja-header-btn" onClick={() => setOpen(false)} data-testid="aja-widget-close" aria-label="Close">
              <X size={14} />
            </button>
          </div>

          <div className="aja-panel-body">
            <p className="aja-section-title">Content</p>
            <Row icon={Type} label="Text Size" desc="Scale reading text up or down" testId="aja-row-font-size">
              <div className="aja-stepper" data-testid="aja-font-stepper">
                <button onClick={() => set({ fontStep: Math.max(0, prefs.fontStep - 1) })} aria-label="Decrease text size">-</button>
                <span>{prefs.fontStep}</span>
                <button onClick={() => set({ fontStep: Math.min(3, prefs.fontStep + 1) })} aria-label="Increase text size">+</button>
              </div>
            </Row>
            <Row icon={AlignJustify} label="Line Height" desc="More space between lines" active={prefs.lineHeight} testId="aja-row-line-height">
              <Switch on={prefs.lineHeight} onClick={() => set({ lineHeight: !prefs.lineHeight })} testId="aja-toggle-line-height" />
            </Row>
            <Row icon={AlignJustify} label="Letter Spacing" desc="Wider spacing between letters" active={prefs.letterSpacing} testId="aja-row-letter-spacing">
              <Switch on={prefs.letterSpacing} onClick={() => set({ letterSpacing: !prefs.letterSpacing })} testId="aja-toggle-letter-spacing" />
            </Row>
            <Row icon={BookOpen} label="Dyslexia Font" desc="Switch to a more readable typeface" active={prefs.dyslexia} testId="aja-row-dyslexia">
              <Switch on={prefs.dyslexia} onClick={() => set({ dyslexia: !prefs.dyslexia })} testId="aja-toggle-dyslexia" />
            </Row>

            <p className="aja-section-title">Color</p>
            <Row icon={Contrast} label="Dark Contrast" desc="High-contrast dark theme" active={prefs.contrast === 'dark'} testId="aja-row-contrast-dark">
              <Switch on={prefs.contrast === 'dark'} onClick={() => set({ contrast: prefs.contrast === 'dark' ? 'none' : 'dark' })} testId="aja-toggle-contrast-dark" />
            </Row>
            <Row icon={Contrast} label="Light Contrast" desc="High-contrast light theme" active={prefs.contrast === 'light'} testId="aja-row-contrast-light">
              <Switch on={prefs.contrast === 'light'} onClick={() => set({ contrast: prefs.contrast === 'light' ? 'none' : 'light' })} testId="aja-toggle-contrast-light" />
            </Row>
            <Row icon={Palette} label="Monochrome" desc="Remove all color from the page" active={prefs.monochrome} testId="aja-row-monochrome">
              <Switch on={prefs.monochrome} onClick={() => set({ monochrome: !prefs.monochrome })} testId="aja-toggle-monochrome" />
            </Row>
            <Row icon={Palette} label="High Saturation" desc="Boost color vibrancy" active={prefs.saturation === 'high'} testId="aja-row-saturation-high">
              <Switch on={prefs.saturation === 'high'} onClick={() => set({ saturation: prefs.saturation === 'high' ? 'none' : 'high' })} testId="aja-toggle-saturation-high" />
            </Row>
            <Row icon={Palette} label="Low Saturation" desc="Reduce color vibrancy" active={prefs.saturation === 'low'} testId="aja-row-saturation-low">
              <Switch on={prefs.saturation === 'low'} onClick={() => set({ saturation: prefs.saturation === 'low' ? 'none' : 'low' })} testId="aja-toggle-saturation-low" />
            </Row>

            <p className="aja-section-title">Navigation</p>
            <Row icon={Link2} label="Highlight Links" desc="Underline & outline every link" active={prefs.highlightLinks} testId="aja-row-highlight-links">
              <Switch on={prefs.highlightLinks} onClick={() => set({ highlightLinks: !prefs.highlightLinks })} testId="aja-toggle-highlight-links" />
            </Row>
            <Row icon={AlignJustify} label="Highlight Headings" desc="Outline every heading" active={prefs.highlightHeadings} testId="aja-row-highlight-headings">
              <Switch on={prefs.highlightHeadings} onClick={() => set({ highlightHeadings: !prefs.highlightHeadings })} testId="aja-toggle-highlight-headings" />
            </Row>
            <Row icon={Keyboard} label="Keyboard Navigation" desc="Visible focus outline everywhere" active={prefs.keyboardNav} testId="aja-row-keyboard-nav">
              <Switch on={prefs.keyboardNav} onClick={() => set({ keyboardNav: !prefs.keyboardNav })} testId="aja-toggle-keyboard-nav" />
            </Row>

            <p className="aja-section-title">Screen Reader</p>
            <Row icon={Ear} label="Read Page Aloud" desc="Text-to-speech for this page" active={reading} testId="aja-row-read-aloud">
              <Switch on={reading} onClick={toggleReadAloud} testId="aja-toggle-read-aloud" />
            </Row>
          </div>

          <div className="aja-panel-footer">
            <img src={LOGO_URL} alt="AI JOOMLA" />
            <a href="https://aijoomla.org/products/ai-joomla-accessibility-widget" target="_blank" rel="noopener noreferrer">
              Web Accessibility By AI JOOMLA
            </a>
          </div>
        </div>
      )}
    </>
  );
}
